import { createContext, useContext, ReactNode } from "react";
import { Profile } from "./types";
import { useProfileData } from "@/hooks/useProfileData";
import { useContactJsonLd } from "@/hooks/useContactJsonLd";

interface ContactDetailsContextType {
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  updateProfile: (updatedProfile: Partial<Profile>) => Promise<Profile>;
}

const ContactDetailsContext = createContext<ContactDetailsContextType | undefined>(undefined);

export const useContactDetails = () => {
  const context = useContext(ContactDetailsContext);
  if (!context) {
    throw new Error("useContactDetails must be used within a ContactDetailsProvider");
  }
  return context;
};

interface ContactDetailsProviderProps {
  contactId: string;
  children: ReactNode;
}

export const ContactDetailsProvider = ({ contactId, children }: ContactDetailsProviderProps) => {
  const { profile, loading, error, refetch, updateProfile } = useProfileData(contactId);
  
  // Add JSON-LD metadata
  useContactJsonLd(profile);

  return (
    <ContactDetailsContext.Provider 
      value={{ 
        profile, 
        loading, 
        error, 
        refetch, 
        updateProfile 
      }}
    >
      {children}
    </ContactDetailsContext.Provider>
  );
};